/** Plain explanations shared by the build and its paged review. */
import {
  correctionNotAppliedCategory,
  type CorrectionNotAppliedCategory,
} from './correction-review';

export type CaptureExclusionReason =
  | 'APPROVAL_RECEIPT_MISSING'
  | 'PAY_FACTS_NOT_READY'
  | 'PAY_ROLE_HELD'
  | 'PAY_RECHECK_REQUIRED';
const COPY: Record<CaptureExclusionReason, { reason: string; nextStep: string }> = {
  APPROVAL_RECEIPT_MISSING: {
    reason:
      'Pay details changed after this timesheet was approved, so its approval is no longer current.',
    nextStep:
      'Reopen the approval and approve again, then build a new version. It is not paid in this build.',
  },
  PAY_FACTS_NOT_READY: {
    reason: 'Allowance or pay answers on this approved timesheet are still waiting for sign-off.',
    nextStep:
      'Finish the allowance sign-off, then build a new version. It is not paid in this build.',
  },
  PAY_RECHECK_REQUIRED: {
    reason: 'The roster changed before this timesheet was approved, so its pay was not rechecked.',
    nextStep:
      'Reopen the timesheet, check its pay, approve it again, then build a new version. It is not paid in this build.',
  },
  PAY_ROLE_HELD: {
    reason: 'The pay role on this approved timesheet has not been settled, so its pay is held.',
    nextStep:
      'Reopen the timesheet, confirm the pay role, approve it again, then build a new version. It is not paid in this build.',
  },
};
export function captureExclusionCopy(code: string): { reason: string; nextStep: string } {
  if (Object.prototype.hasOwnProperty.call(COPY, code)) return COPY[code as CaptureExclusionReason];
  return {
    reason: 'This timesheet was held out of the build.',
    nextStep: 'Review this timesheet before building a new version.',
  };
}

const CORRECTION_REASON: Record<CorrectionNotAppliedCategory, string> = {
  review_evidence:
    'The approved correction to this shift is missing its reviewer’s sign-off details.',
  corrected_times:
    'The corrected start, finish or break on the approved correction does not add up to its hours.',
  allowances:
    'An allowance on the approved correction does not meet the allowance rules for this shift.',
  allowance_total:
    'The allowances the approved correction replaces do not match the allowance total saved on the timesheet.',
  leave_missing:
    'The approved correction replaces this shift with leave, but the leave request is missing.',
  duplicate:
    'This shift has more than one approved correction, so Statura cannot tell which one applies.',
  other: 'Statura could not check the approved correction to this shift.',
};

/**
 * Review item for an approved correction the build could not use. The shift
 * is paid as approved; the officer decides whether pay should change.
 */
export function correctionNotAppliedCopy(
  warning: string,
): { reason: string; nextStep: string } | null {
  const category = correctionNotAppliedCategory(warning);
  if (!category) return null;
  return {
    reason: CORRECTION_REASON[category],
    nextStep:
      'Statura paid the timesheet as it was approved, without the correction. Open the timesheet and check the correction. If pay should change, fix the correction and pay the difference in a later pay run.',
  };
}

/** Held source: the correction failed, and the approved timesheet can't be priced or the
 * correction would have stopped paying it (a duplicate, or leave instead of work). */
export const CORRECTION_HOLD_COPY = {
  reason:
    'An approved correction to this timesheet failed Statura’s checks, and Statura cannot safely pay the timesheet without it. Only this shift is held; the worker’s other pay is not affected.',
  nextStep: 'Open the timesheet and review the correction.',
};
